import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  PlusIcon,
  BuildingIcon,
  WalletIcon,
  Trash2Icon,
  EditIcon,
  SearchIcon,
  PaperclipIcon,
  FileTextIcon,
  ExternalLinkIcon,
  FilterIcon,
  XCircleIcon,
  DownloadIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  Loader2Icon,
} from 'lucide-react';
import { toast } from 'sonner';
import SoloExportModal from './SoloExportModal';
import SoloExpenseEditModal from './SoloExpenseEditModal';
import BelgeOnizlemeModal from './BelgeOnizlemeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

const fmt = (amount, currency = 'TRY') => {
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount || 0);
  } catch { return `${amount} ${currency}`; }
};

const formatBytes = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Küçük ek dosya rozeti — tıklandığında BelgeOnizlemeModal'ı açar.
const AttachmentBadge = ({ file, rowId, onOpen }) => {
  if (!file?.url) return <span className="text-gray-300">—</span>;
  const isImage = (file.type || '').startsWith('image/');
  return (
    <div className="relative inline-block group" data-testid={`solo-attachment-${rowId}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpen && onOpen(file); }}
        className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-purple-50 text-purple-600 hover:bg-purple-100 transition"
        aria-label={`Belge: ${file.name}`}
        data-testid={`solo-attachment-link-${rowId}`}
      >
        <PaperclipIcon className="w-4 h-4" />
      </button>
      <div
        className="absolute z-30 right-0 top-full mt-1 w-64 p-3 rounded-lg shadow-xl bg-white border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none"
        role="tooltip"
        data-testid={`solo-attachment-preview-${rowId}`}
      >
        <div className="flex items-start gap-2 mb-2">
          {isImage ? (
            <img
              src={file.url}
              alt={file.name}
              className="w-12 h-12 object-cover rounded-md flex-shrink-0 border border-gray-100"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="w-12 h-12 rounded-md bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
              <FileTextIcon className="w-6 h-6 text-gray-400" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-900 truncate" title={file.name}>
              {file.name || 'Belge'}
            </p>
            <p className="text-[10px] text-gray-500 mt-0.5">
              {formatBytes(file.size)}
              {file.type ? ` · ${file.type.split('/')[1] || file.type}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-purple-600">
          <ExternalLinkIcon className="w-3 h-3" />
          <span>Önizlemeyi aç</span>
        </div>
      </div>
    </div>
  );
};

// Ödeme türü rozeti — Nakit veya Kart adı+son 4 hane ile küçük bir chip.
const PaymentBadge = ({ row }) => {
  const method = row.payment_method || 'cash';
  if (method === 'credit_card') {
    const c = row.credit_card;
    const label = c ? `${c.card_name} •${c.last4}` : 'Kredi Kartı';
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100"
        title={label}
      >
        💳 <span className="truncate max-w-[120px]">{label}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
      💵 Nakit
    </span>
  );
};

const MONTH_LABELS = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

// Compact page number list — shows first, last, current and its neighbours,
// with ellipsis (…) filler. Keeps the paginator readable at 100+ pages.
const buildPageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = Array.from(pages).filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
  const withGaps = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) withGaps.push('…');
    withGaps.push(sorted[i]);
  }
  return withGaps;
};

const SoloExpenseList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Which tab is currently visible. Drives which expense_type gets sent to
  // the paginated list endpoint.
  const [activeTab, setActiveTab] = useState('company');

  // Data state (server-driven)
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ years: [], categories: [], suppliers: [] });
  const [kpi, setKpi] = useState({ company_total: 0, personal_total: 0, company_count: 0, personal_count: 0 });

  // Filters
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');

  // Per-tab paginators so switching tabs remembers the previous page.
  const [companyPage, setCompanyPage] = useState(1);
  const [personalPage, setPersonalPage] = useState(1);
  const currentPage = activeTab === 'company' ? companyPage : personalPage;
  const setCurrentPage = (p) => (activeTab === 'company' ? setCompanyPage(p) : setPersonalPage(p));

  // Modals
  const [editingExpense, setEditingExpense] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  // Deep-link support from İşletmeler (?supplier=Migros). Runs once.
  useEffect(() => {
    const fromUrl = searchParams.get('supplier');
    if (fromUrl && fromUrl !== supplierFilter) {
      setSupplierFilter(fromUrl);
    }
  }, []);

  // Debounce search input — 300ms is short enough to feel instant but long
  // enough to avoid a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Reset paginator whenever a filter/search/tab changes so the user never
  // lands on an out-of-range page.
  useEffect(() => {
    setCompanyPage(1);
    setPersonalPage(1);
  }, [debouncedQuery, yearFilter, monthFilter, categoryFilter, supplierFilter, paymentMethodFilter]);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        expense_type: activeTab,
        page: currentPage,
        page_size: PAGE_SIZE,
      };
      if (debouncedQuery) params.q = debouncedQuery;
      if (yearFilter !== 'all') params.year = yearFilter;
      if (monthFilter !== 'all') params.month = monthFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (supplierFilter !== 'all') params.supplier = supplierFilter;
      if (paymentMethodFilter !== 'all') params.payment_method = paymentMethodFilter;
      const res = await axios.get(`${BACKEND_URL}/api/solo/expenses`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setRows(res.data.expenses || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
      if (res.data.facets) setFacets(res.data.facets);
      if (res.data.kpi) setKpi(res.data.kpi);
    } catch (e) {
      console.error(e);
      toast.error('Harcamalar alınamadı');
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, debouncedQuery, yearFilter, monthFilter, categoryFilter, supplierFilter, paymentMethodFilter]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const del = async (id) => {
    if (!window.confirm('Bu harcamayı silmek istediğinize emin misiniz?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/solo/expenses/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Silindi');
      // If we just removed the last row on a page, step back one page.
      if (rows.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchExpenses();
      }
    } catch (e) {
      toast.error('Silinemedi');
    }
  };

  const hasActiveFilter = yearFilter !== 'all' || monthFilter !== 'all'
    || categoryFilter !== 'all' || supplierFilter !== 'all'
    || paymentMethodFilter !== 'all' || !!query;
  const clearAllFilters = () => {
    setYearFilter('all');
    setMonthFilter('all');
    setCategoryFilter('all');
    setSupplierFilter('all');
    setPaymentMethodFilter('all');
    setQuery('');
    // Also drop any deep-link query params so the URL matches the UI.
    if (searchParams.get('supplier')) {
      searchParams.delete('supplier');
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Reusable row renderer
  const renderRow = (e) => (
    <tr key={e.id} className="border-b last:border-0 hover:bg-gray-50">
      <td className="px-4 py-3 text-gray-600">{e.date}</td>
      <td className="px-4 py-3 font-medium text-gray-900">{e.supplier || '—'}</td>
      <td className="px-4 py-3 text-gray-600">{e.description || '—'}</td>
      <td className="px-4 py-3 text-gray-600">{e.category || '—'}</td>
      <td className="px-4 py-3">
        <PaymentBadge row={e} />
      </td>
      <td className="px-4 py-3 text-center">
        <AttachmentBadge file={e.attached_file} rowId={e.id} onOpen={setPreviewFile} />
      </td>
      <td className="px-4 py-3 text-right font-medium text-gray-900">{fmt(e.amount, e.currency)}</td>
      <td className="px-4 py-3 text-center">
        <div className="inline-flex items-center gap-1">
          <button
            onClick={() => setEditingExpense(e)}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded transition"
            data-testid={`solo-edit-${e.id}`}
            aria-label="Düzenle"
            title="Düzenle"
          >
            <EditIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => del(e.id)}
            className="p-1 text-red-500 hover:bg-red-50 rounded transition"
            data-testid={`solo-delete-${e.id}`}
            aria-label="Sil"
            title="Sil"
          >
            <Trash2Icon className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  const renderMobileCard = (e) => (
    <div
      key={e.id}
      className="p-4 border-b last:border-0 hover:bg-gray-50"
      data-testid={`solo-expense-card-${e.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-gray-900 break-words">{e.supplier || '—'}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            {e.date}
            {e.category && <span> • {e.category}</span>}
          </div>
        </div>
        <div className="text-right font-bold text-gray-900 tabular-nums whitespace-nowrap">
          {fmt(e.amount, e.currency)}
        </div>
      </div>
      {e.description && (
        <div className="text-xs text-gray-600 mb-2 break-words">{e.description}</div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <AttachmentBadge file={e.attached_file} rowId={e.id} onOpen={setPreviewFile} />
          <PaymentBadge row={e} />
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setEditingExpense(e)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
            data-testid={`solo-edit-mobile-${e.id}`}
            aria-label="Düzenle"
          >
            <EditIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => del(e.id)}
            className="p-2 text-red-500 hover:bg-red-50 rounded transition"
            data-testid={`solo-delete-mobile-${e.id}`}
            aria-label="Sil"
          >
            <Trash2Icon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const emptyText = activeTab === 'company'
    ? (hasActiveFilter ? 'Bu filtrelere uyan şirket harcaması yok.' : 'Henüz şirket harcaması yok.')
    : (hasActiveFilter ? 'Bu filtrelere uyan kişisel ödeme yok.' : 'Henüz kişisel ödeme yok.');
  const accentColor = activeTab === 'company' ? 'bg-blue-50/40' : 'bg-emerald-50/40';

  const renderTableBody = () => {
    if (loading) {
      return (
        <div className="text-center py-10 text-gray-500 flex items-center justify-center gap-2" data-testid="solo-list-loading">
          <Loader2Icon className="w-4 h-4 animate-spin" /> Yükleniyor...
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div className="text-center py-10 text-gray-400 px-4" data-testid="solo-list-empty">
          {emptyText}{' '}
          <button
            onClick={() => navigate('/dashboard/expenses/new')}
            className="text-purple-600 hover:underline"
          >
            Yeni harcama ekleyin
          </button>.
        </div>
      );
    }
    return (
      <>
        <div className="md:hidden" data-testid={`solo-${activeTab}-mobile-list`}>
          {rows.map(renderMobileCard)}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className={`${accentColor} border-b`}>
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Tarih</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">İşletme</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Açıklama</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Kategori</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Ödeme</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-700">Belge</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Tutar</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-700">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(renderRow)}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const pageWindow = useMemo(() => buildPageWindow(currentPage, totalPages), [currentPage, totalPages]);
  const startIndex = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, total);

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6" data-testid="solo-expense-list">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">Tüm Harcamalarım</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Şirket harcamaları ve kişisel ödemeleriniz ayrı sekmelerde</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setShowExportModal(true)}
            data-testid="solo-export-btn"
            className="flex-1 sm:flex-initial"
          >
            <DownloadIcon className="w-4 h-4 mr-2" /> <span className="hidden xs:inline">Rapor İndir</span><span className="xs:hidden">Rapor</span>
          </Button>
          <Button onClick={() => navigate('/dashboard/expenses/new')} data-testid="solo-new-expense-btn" className="flex-1 sm:flex-initial">
            <PlusIcon className="w-4 h-4 mr-2" /> <span className="hidden xs:inline">Yeni Harcama</span><span className="xs:hidden">Yeni</span>
          </Button>
        </div>
      </div>

      {/* KPI cards — pulled from server aggregation so they reflect the full
          dataset, not just the current page. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4" data-testid="solo-kpi-company">
          <div className="text-xs text-gray-500 flex items-center gap-1.5">
            <BuildingIcon className="w-3.5 h-3.5" /> Şirket Harcamaları
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{fmt(kpi.company_total)}</div>
          <div className="text-xs text-gray-400 mt-1">{kpi.company_count} kayıt</div>
        </Card>
        <Card className="p-4" data-testid="solo-kpi-personal">
          <div className="text-xs text-gray-500 flex items-center gap-1.5">
            <WalletIcon className="w-3.5 h-3.5" /> Şahsi Harcamalar
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{fmt(kpi.personal_total)}</div>
          <div className="text-xs text-gray-400 mt-1">{kpi.personal_count} kayıt</div>
        </Card>
      </div>

      {/* Filters bar */}
      <Card className="p-4" data-testid="solo-filters-bar">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-700">
          <FilterIcon className="w-4 h-4 text-gray-500" />
          Filtreler
          {hasActiveFilter && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="ml-auto text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
              data-testid="solo-filters-clear"
            >
              <XCircleIcon className="w-3.5 h-3.5" />
              Filtreleri temizle
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Yıl</label>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-purple-500 bg-white"
              data-testid="solo-filter-year"
            >
              <option value="all">Tüm yıllar</option>
              {facets.years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Ay</label>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-purple-500 bg-white"
              data-testid="solo-filter-month"
            >
              <option value="all">Tüm aylar</option>
              {MONTH_LABELS.map((label, i) => {
                const mm = String(i + 1).padStart(2, '0');
                return <option key={mm} value={mm}>{label}</option>;
              })}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Kategori</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-purple-500 bg-white"
              data-testid="solo-filter-category"
            >
              <option value="all">Tüm kategoriler</option>
              {facets.categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">İşletme</label>
            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-purple-500 bg-white"
              data-testid="solo-filter-supplier"
            >
              <option value="all">Tüm işletmeler</option>
              {facets.suppliers.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Ödeme Türü</label>
            <select
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-purple-500 bg-white"
              data-testid="solo-filter-payment-method"
            >
              <option value="all">Tümü</option>
              <option value="cash">💵 Nakit</option>
              <option value="credit_card">💳 Kredi Kartı</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Ara</label>
            <div className="relative">
              <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="İşletme, açıklama..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 h-10"
                data-testid="solo-search"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs — Şirket / Şahsi */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-11 bg-gray-100" data-testid="solo-expense-tabs">
          <TabsTrigger
            value="company"
            className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-blue-700"
            data-testid="solo-tab-company"
          >
            <BuildingIcon className="w-4 h-4" />
            Şirket Harcamaları
            <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
              {kpi.company_count}
            </span>
          </TabsTrigger>
          <TabsTrigger
            value="personal"
            className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-emerald-700"
            data-testid="solo-tab-personal"
          >
            <WalletIcon className="w-4 h-4" />
            Şahsi Harcamalar
            <span className="ml-1 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
              {kpi.personal_count}
            </span>
          </TabsTrigger>
        </TabsList>

        {/* Single content block — the table body queries server-side using
            `activeTab` so we only render once, not per-tab. */}
        <TabsContent value={activeTab} className="mt-4">
          <Card className="overflow-hidden" data-testid={`solo-list-${activeTab}`}>
            <div className={`px-4 py-3 border-b flex items-center justify-between ${activeTab === 'company' ? 'bg-blue-50 border-blue-100' : 'bg-emerald-50 border-emerald-100'}`}>
              <h2 className={`text-base font-semibold flex items-center gap-2 ${activeTab === 'company' ? 'text-blue-900' : 'text-emerald-900'}`}>
                {activeTab === 'company' ? <BuildingIcon className="w-4 h-4" /> : <WalletIcon className="w-4 h-4" />}
                {activeTab === 'company' ? 'Şirket Harcamaları' : 'Şahsi Harcamalar'}
              </h2>
              <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'company' ? 'text-blue-700 bg-blue-100' : 'text-emerald-700 bg-emerald-100'}`}>
                {total} kayıt
              </span>
            </div>

            {renderTableBody()}

            {/* Paginator */}
            {!loading && total > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-600" data-testid="solo-list-paginator">
                <span data-testid="solo-list-range">
                  {startIndex}–{endIndex} / {total} kayıt
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="solo-page-first"
                    aria-label="İlk sayfa"
                  >
                    <ChevronsLeftIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="solo-page-prev"
                    aria-label="Önceki sayfa"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>

                  {pageWindow.map((p, idx) => (
                    p === '…' ? (
                      <span key={`gap-${idx}`} className="px-2 text-gray-400">…</span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setCurrentPage(p)}
                        className={`min-w-[32px] px-2 py-1 rounded border text-xs font-medium ${
                          p === currentPage
                            ? (activeTab === 'company' ? 'bg-blue-600 text-white border-blue-600' : 'bg-emerald-600 text-white border-emerald-600')
                            : 'bg-white border-gray-200 hover:bg-gray-100 text-gray-700'
                        }`}
                        data-testid={`solo-page-${p}`}
                      >
                        {p}
                      </button>
                    )
                  ))}

                  <button
                    type="button"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                    className="p-1.5 rounded border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="solo-page-next"
                    aria-label="Sonraki sayfa"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage >= totalPages}
                    className="p-1.5 rounded border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="solo-page-last"
                    aria-label="Son sayfa"
                  >
                    <ChevronsRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {showExportModal && (
        <SoloExportModal onClose={() => setShowExportModal(false)} />
      )}

      {editingExpense && (
        <SoloExpenseEditModal
          expense={editingExpense}
          onClose={() => setEditingExpense(null)}
          onSaved={() => { setEditingExpense(null); fetchExpenses(); }}
        />
      )}

      {previewFile && (
        <BelgeOnizlemeModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}
    </div>
  );
};

export default SoloExpenseList;
